import React, { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { GlgDatePicker, GlgSelect } from 'Shared/components'
import { useWhsTerms } from '../whs_terms_context'

const t = (key, opts) => !window.I18n ? '' : window.I18n.t(key, opts)

// Slope 0 makes HS divide by zero and render "+Infinity" (RTM-10256).
// Mirrored server-side in HandicapServer::ScoreParamsValidator.
export const SLOPE_MIN = 55
export const SLOPE_MAX = 155

export const calcDiff = (score, rating, slope) => {
  const s = parseFloat(score)
  const r = parseFloat(rating)
  const sl = parseFloat(slope)
  if (isNaN(s) || isNaN(r) || isNaN(sl) || sl === 0) return ''
  return ((s - r) * 113.0 / sl).toFixed(1)
}

// Number(), not parseFloat(): "12abc" must be rejected, not read as 12.
const numberOrNull = (value) => {
  const str = String(value ?? '').trim()
  if (str === '') return null
  const num = Number(str)
  return Number.isFinite(num) ? num : null
}

const label = (key, whsTerms) => t(`member_handicap_history.common.${key}`, whsTerms)
const errorFor = (key, opts) => t(`member_handicap_history.errors.${key}`, opts)

// game_type is checked server-side only — the Holes select can't produce a bad value.
export const validateScore = (score, whsTerms) => {
  const errors = {}

  if (!score.play_at) errors.play_at = errorFor('blank', { label: label('date') })
  if (!String(score.course ?? '').trim()) errors.course = errorFor('blank', { label: label('course') })
  if (!String(score.tee ?? '').trim()) errors.tee = errorFor('blank', { label: label('tee') })

  const slope = numberOrNull(score.slope)
  if (slope === null || !Number.isInteger(slope) || slope < SLOPE_MIN || slope > SLOPE_MAX) {
    errors.slope = errorFor('out_of_range', {
      label: label('slope', whsTerms), min: SLOPE_MIN, max: SLOPE_MAX,
    })
  }

  const rating = numberOrNull(score.rating)
  if (rating === null || rating <= 0) {
    errors.rating = errorFor('positive_number', { label: label('rating', whsTerms) })
  }

  const strokes = numberOrNull(score.score)
  if (strokes === null || !Number.isInteger(strokes) || strokes <= 0) {
    errors.score = errorFor('positive_integer', { label: label('score', whsTerms) })
  }

  return errors
}

const slopeRatingFor = (tee, gameType) => {
  if (gameType === '9') {
    return {
      slope:  tee.slope_front9  ?? tee.slope_back9  ?? tee.slope_18  ?? '',
      rating: tee.rating_front9 ?? tee.rating_back9 ?? tee.rating_18 ?? '',
    }
  }
  return { slope: tee.slope_18 ?? '', rating: tee.rating_18 ?? '' }
}

const defaultScore = (courses) => {
  const firstCourse = courses && courses[0]
  const firstTee = firstCourse && firstCourse.tees && firstCourse.tees[0]
  const slope = firstTee ? (firstTee.slope_18 ?? '') : ''
  const rating = firstTee ? (firstTee.rating_18 ?? '') : ''
  return {
    play_at: '',
    course: firstCourse ? firstCourse.name : '',
    tee: firstTee ? firstTee.name : '',
    slope,
    rating,
    score: '',
    game_type: '18',
  }
}

const FIELD_ORDER = [ 'play_at', 'course', 'tee', 'slope', 'rating', 'score' ]

// One block below the row, not a span per input: showing messages must not
// resize or move any field. Ids let the inputs point here via aria-describedby.
const ErrorSummary = ({ errors }) => {
  const fields = FIELD_ORDER.filter(field => errors[field])
  if (fields.length === 0) return null

  return (
    <div className='add-scores-form__errors' role='alert'>
      {fields.map(field => (
        <div key={field} id={`add-score-${field}-error`} className='add-scores-form__error'>
          {errors[field]}
        </div>
      ))}
    </div>
  )
}

ErrorSummary.propTypes = {
  errors: PropTypes.object.isRequired,
}

const NumericField = ({ field, fieldLabel, value, error, step, widthClass, onChange }) => (
  <div className={`add-score-row__field ${widthClass}${error ? ' has-error' : ''}`}>
    <label className='add-score-row__label' htmlFor={`add-score-${field}`}>{fieldLabel}</label>
    <input
      id={`add-score-${field}`}
      type='number'
      step={step}
      className='form-control input-sm'
      placeholder={fieldLabel}
      value={value ?? ''}
      aria-invalid={error ? 'true' : 'false'}
      aria-describedby={error ? `add-score-${field}-error` : undefined}
      onChange={e => onChange(e.target.value)}
    />
  </div>
)

NumericField.propTypes = {
  field: PropTypes.string.isRequired,
  fieldLabel: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.string,
  step: PropTypes.string,
  widthClass: PropTypes.string,
  onChange: PropTypes.func.isRequired,
}

// A single score-entry form: the golfer adds one score at a time, so the fields
// and their derived slope/rating live directly here (no per-row abstraction).
const AddScoresForm = ({ onSave, onCancel, saving, courses }) => {
  const whsTerms = useWhsTerms()
  const [score, setScore] = useState(() => defaultScore(courses))
  // Fields invalid at the last Save. Only Save adds to this, so editing can
  // never raise a message for a field the user hasn't tried to save yet.
  const [reported, setReported] = useState([])

  const set = (field, value) => setScore(prev => ({ ...prev, [field]: value }))

  const slopeLabel = t('member_handicap_history.common.slope', whsTerms)
  const ratingLabel = t('member_handicap_history.common.rating', whsTerms)

  const selectedCourse = courses.find(c => c.name === score.course) || null
  const teesForCourse = selectedCourse ? selectedCourse.tees : []
  // Derived from the selected tee name — not state (REACT-089).
  const selectedTee = teesForCourse.find(tee => tee.name === score.tee) || null

  const errors = validateScore(score, whsTerms)
  // Fixing a reported field clears it immediately; nothing new appears.
  const visibleErrors = reported.reduce((shown, field) => (
    errors[field] ? { ...shown, [field]: errors[field] } : shown
  ), {})
  const visibleError = (field) => visibleErrors[field]

  const handleCourseChange = (courseName) => {
    set('course', courseName)

    const newCourse = courses.find(c => c.name === courseName) || null
    const firstTee = (newCourse && newCourse.tees && newCourse.tees[0]) || null

    set('tee', firstTee ? firstTee.name : '')

    if (firstTee) {
      const { slope, rating } = slopeRatingFor(firstTee, score.game_type)
      set('slope', slope)
      set('rating', rating)
    } else {
      set('slope', '')
      set('rating', '')
    }
  }

  const handleTeeChange = (teeName) => {
    const tee = teesForCourse.find(t => t.name === teeName) || null
    set('tee', teeName)
    if (tee) {
      const { slope, rating } = slopeRatingFor(tee, score.game_type)
      set('slope', slope)
      set('rating', rating)
    }
  }

  const handleGameTypeChange = (gt) => {
    set('game_type', gt)
    if (selectedTee) {
      const { slope, rating } = slopeRatingFor(selectedTee, gt)
      set('slope', slope)
      set('rating', rating)
    }
  }

  const handleSave = () => {
    const invalid = Object.keys(errors)
    setReported(invalid)
    if (invalid.length > 0) return

    onSave(score)
  }

  const hasCourses = courses.length > 0
  // Enabled until a Save attempt reports something — that first click is what
  // surfaces the messages. After that it stays disabled until they all clear.
  const canSave = !saving && Object.keys(visibleErrors).length === 0

  return (
    <div className='add-scores-form'>
      <div className='add-scores-form__header'>
        <h5>{t('member_handicap_history.add_scores_form.title')}</h5>
        <span className='add-scores-form__diff'>
          {t('member_handicap_history.add_scores_form.diff_preview', { value: calcDiff(score.score, score.rating, score.slope) || '—' })}
        </span>
      </div>

      <div className='add-score-row'>
        {/* Line 1: Date, Course, Tee */}
        <div className='add-score-row__line add-score-row__line--top'>
          <div className={`add-score-row__field add-score-row__field--date${visibleError('play_at') ? ' has-error' : ''}`}>
            <label className='add-score-row__label'>{t('member_handicap_history.common.date')}</label>
            <div className='add-score-date-picker'>
              <GlgDatePicker
                name='play_at'
                format='MMM D, YYYY'
                value={score.play_at ? moment(score.play_at, 'YYYY-MM-DD').format('MMM D, YYYY') : ''}
                onChange={(formatted) => set('play_at', formatted ? moment(formatted, 'MMM D, YYYY').format('YYYY-MM-DD') : '')}
              />
            </div>
          </div>

          <div className={`add-score-row__field add-score-row__field--course${visibleError('course') ? ' has-error' : ''}`}>
            <label className='add-score-row__label'>{t('member_handicap_history.common.course')}</label>
            {hasCourses ? (
              <GlgSelect
                name='course'
                items={courses.map(c => ({ value: c.name, label: c.name }))}
                value={score.course || ''}
                onChange={(value) => handleCourseChange(value)}
                classNamePrefix='mhh-select'
                usePortal
              />
            ) : (
              <input
                type='text'
                className='form-control input-sm'
                placeholder={t('member_handicap_history.common.course')}
                value={score.course || ''}
                onChange={e => set('course', e.target.value)}
              />
            )}
          </div>

          <div className={`add-score-row__field add-score-row__field--tee${visibleError('tee') ? ' has-error' : ''}`}>
            <label className='add-score-row__label'>{t('member_handicap_history.common.tee')}</label>
            {hasCourses ? (
              <GlgSelect
                name='tee'
                items={teesForCourse.map(t => ({ value: t.name, label: t.name }))}
                value={score.tee || ''}
                onChange={(value) => handleTeeChange(value)}
                disabled={!score.course}
                classNamePrefix='mhh-select'
                usePortal
              />
            ) : (
              <input
                type='text'
                className='form-control input-sm'
                placeholder={t('member_handicap_history.common.tee')}
                value={score.tee || ''}
                onChange={e => set('tee', e.target.value)}
              />
            )}
          </div>
        </div>

        {/* Line 2: Slope, Rating, Score, Holes */}
        <div className='add-score-row__line'>
          <NumericField
            field='slope'
            fieldLabel={slopeLabel}
            value={score.slope}
            error={visibleError('slope')}
            widthClass='add-score-row__field--narrow'
            onChange={value => set('slope', value)}
          />

          <NumericField
            field='rating'
            fieldLabel={ratingLabel}
            value={score.rating}
            error={visibleError('rating')}
            step='0.1'
            widthClass='add-score-row__field--medium'
            onChange={value => set('rating', value)}
          />

          <NumericField
            field='score'
            fieldLabel={t('member_handicap_history.common.score')}
            value={score.score}
            error={visibleError('score')}
            widthClass='add-score-row__field--narrow'
            onChange={value => set('score', value)}
          />

          <div className='add-score-row__field add-score-row__field--narrow'>
            <label className='add-score-row__label'>{t('member_handicap_history.common.holes')}</label>
            <GlgSelect
              name='game_type'
              items={[{ value: '18', label: '18' }, { value: '9', label: '9' }]}
              value={score.game_type || '18'}
              onChange={(value) => handleGameTypeChange(value)}
              classNamePrefix='mhh-select'
              usePortal
            />
          </div>

          <div className='add-score-row__actions-cell'>
            <button type='button' className='btn btn-default btn-sm' onClick={onCancel} disabled={saving}>{t('member_handicap_history.common.cancel')}</button>
            <button type='button' className='btn btn-sm btsp_orange_button' onClick={handleSave} disabled={!canSave}>
              {saving ? t('member_handicap_history.add_scores_form.saving') : t('member_handicap_history.add_scores_form.save_score')}
            </button>
          </div>
        </div>
      </div>

      <ErrorSummary errors={visibleErrors} />
    </div>
  )
}

AddScoresForm.defaultProps = {
  courses: [],
}

AddScoresForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  courses: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    tees: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      slope_18: PropTypes.number,
      rating_18: PropTypes.number,
      slope_front9: PropTypes.number,
      rating_front9: PropTypes.number,
      slope_back9: PropTypes.number,
      rating_back9: PropTypes.number,
    })),
  })),
}

export default AddScoresForm
